import { lazy, useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { Helmet } from "react-helmet"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Divider, Label, Message } from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"
import styled from "styled-components"

import actions from "actions"
import api, { asQuery } from "api"
import ChallengeIPStatementAgreement from "components/lib/ChallengeIPStatementAgreement"
import ChallengeStatusBar from "components/lib/ChallengeStatusBar"
import PageLoadError from "components/lib/PageLoadError"
import ReportContentChip from "components/lib/ReportContent/ReportContentChip"
import { SideNav } from "components/lib/SideNav"
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import constants from "utils/constants"
import { useUserReload } from "utils/useUserReload"
import util from "utils/utils"

import ChallengeAssessments from "./Assessments"
import ChallengeHeader from "./Header"
import ChallengeIdeas from "./Ideas"
import NewIdea from "./NewIdea"
import ChallengeOverview from "./Overview"
import ChallengePlanner from "./Planner/PlannerMenu"

const ChallengeActivity = lazy(() => import("./Activity"))
const ChallengeQuestions = lazy(() => import("./Questions/Questions"))

const StyledLabel = styled(Label)`
  line-height: 1.1em !important;
`

// Wrapper func component for when visiting the project ideas page
// Ensures that if you visit challenges/<challenge_id>/board, the view type is set to board
const ChallengeIdeasProjectWrapper = ({ children }) => {
  const dispatch = useDispatch()
  const updateIdeaViewType = useCallback(
    (newIdeaViewType) => dispatch(actions.challenges.setIdeaViewType(newIdeaViewType)),
    [dispatch],
  )

  useEffect(() => {
    updateIdeaViewType("board")
  }, [updateIdeaViewType])

  return children
}

const Challenge = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const [_searchParams, setSearchParams] = useSearchParams()
  const params = useParams()
  const challengeId = params.id?.replace("assessments", "")

  const ideaViewType = useAppSelector(({ challenges }) => challenges.ideaViewType)
  const user = useAppSelector((state) => state.user)
  const urlOrganisation = useAppSelector((state) => state.organisations?.urlOrganisation)

  const dispatch = useDispatch()
  const reloadUser = useUserReload()
  const addEngagement = useCallback((engagement) => dispatch(actions.user.addEngagement(engagement)), [dispatch])
  const updateFilters = useCallback((data) => dispatch(actions.challenges.updateIdeaFilters(data)), [dispatch])
  const onFollow = useCallback((context, id) => dispatch(actions.user.onFollow(context, id)), [dispatch])
  const updateIdeaViewType = useCallback(
    (newIdeaViewType) => dispatch(actions.challenges.setIdeaViewType(newIdeaViewType)),
    [dispatch],
  )

  const [popularTags, setPopularTags] = useState([])
  const [allStamps, setAllStamps] = useState([])
  const [availableGroups, setAvailableGroups] = useState([])
  const [availableOrganisations, setAvailableOrganisations] = useState([])
  const [firstView, setFirstView] = useState(true)
  const [hasSignedStatement, setHasSignedStatement] = useState(false)
  const [hasClickedNewIdea, setHasClickedNewIdea] = useState(false)
  const [creatingNewIdea, setCreatingNewIdea] = useState(false)
  const [ipManagementInfo, setIpManagementInfo] = useState({
    userHasSigned: false,
    hasSensitiveInfo: false,
    ipStatementEnabled: false,
    userSignature: null,
  })
  const [ipStatement, setIpStatement] = useState(null)
  const [ipStatementAgreementOpen, setIpStatementAgreementOpen] = useState(false)
  const userId = user?._id
  const orgIpManagementEnabled = util.organisationFeaturesEnabled(user, ["ipManagement"])
  const challengePlannerEnabled = util.organisationFeaturesEnabled(user, ["challengePlanner"])

  const challengeQuery = useQuery({
    queryKey: ["challenges", challengeId],
    queryFn: asQuery(api.challenges.get, {
      params: [challengeId],
    }),
  })
  const challenge = challengeQuery.data
  const canManageChallenge = useMemo(() => util.canManageChallenge(user, challenge), [user, challenge])

  const myIdeasQuery = useQuery({
    queryKey: ["challenges", challengeId, "ideas", userId],
    queryFn: asQuery(api.challenges.getIdeas, {
      params: [challengeId, { scope: "me" }],
    }),
    enabled: !!userId && !!challengeId,
  })
  const myIdeas = myIdeasQuery.data?.ideas || []

  const assignedAssessmentQuery = useQuery({
    queryKey: ["challenges", challengeId, "assessments"],
    queryFn: asQuery(api.challenges.getAssignedAssessments, {
      params: [challengeId],
    }),
    enabled: !!userId,
  })
  const myAssignedAssessments = assignedAssessmentQuery.data?.ideas || []

  /*
    Auto collapse the sidebar on some pages
  */
  const [isCollapsed, setIsCollapsed] = useState(undefined)
  useEffect(() => {
    setIsCollapsed((prev) => {
      if (location.pathname.includes("/ideas")) {
        if (!theme.sizes.isLargeComputer) {
          return true
        }
      }
      return prev
    })
  }, [location, theme])

  useEffect(() => {
    if (firstView) {
      window.scrollTo(0, 0)
      setFirstView(false)
    }
  }, [firstView])

  useEffect(() => {
    if (userId) {
      addEngagement("viewedChallenge")
    }
  }, [userId, addEngagement])

  useEffect(() => {
    updateFilters({
      prevViewed: challenge?._id,
      ideaPage: 1,
      ideaOrder: challenge?.defaultIdeaOrder || "default",
      tagFilter: [],
      groupFilter: [],
      challengeFilter: [],
      ideaFieldChoices: [],
      ideaFilter: "",
    })
  }, [challenge, updateFilters])

  const getLatestIpStatementVersion = useCallback(() => {
    api.challenges.getLatestIpStatement(
      challengeId,
      ({ userHasSigned, hasSensitiveInfo, ipStatementEnabled, activeStatement, userSignature }) => {
        if (ipStatementEnabled) {
          setIpManagementInfo({
            userHasSigned,
            hasSensitiveInfo,
            ipStatementEnabled,
            userSignature,
          })
          setHasSignedStatement(userHasSigned)
          setIpStatement(activeStatement)
        }
      },
      () => {},
    )
  }, [challengeId])

  const { ipManagement = {} } = challenge || {}
  useEffect(() => {
    if (orgIpManagementEnabled && ipManagement?.isEnabled) {
      getLatestIpStatementVersion()
    }
  }, [getLatestIpStatementVersion, ipManagement, orgIpManagementEnabled])

  const assignIdea = useCallback(
    (idea) => {
      if (!userId) return

      let authToken = null
      if (util.localStorageIsSupported()) {
        authToken = localStorage.getItem(`ideaAuthToken:${idea?._id}`)
      }
      if (userId && !idea?.user && authToken && idea?.authToken === authToken) {
        const data = { authToken }
        api.ideas.assign(
          idea?._id,
          data,
          () => {
            localStorage.removeItem(`ideaAuthToken:${idea?._id}`)
            localStorage.removeItem(`challengeIdeas:${challengeId}`)
          },
          () => {},
        )
      }
    },
    [userId, challengeId],
  )

  useEffect(() => {
    if (!challengeId) {
      return
    }
    if (!util.localStorageIsSupported()) {
      return
    }
    const challengeIdeas = localStorage.getItem(`challengeIdeas:${challengeId}`)
    if (!challengeIdeas) {
      return
    }

    const ideaIds = challengeIdeas.split("::")
    if (ideaIds.length === 1 && !location.search.includes("noRedirect")) {
      const authToken = localStorage.getItem(`ideaAuthToken:${ideaIds[0]}`)
      api.ideas.get(
        ideaIds[0],
        authToken,
        (idea) => {
          if (!userId) {
            if (!idea.isSubmitted) {
              navigate(`/ideas/${ideaIds[0]}`)
            }
          } else {
            myIdeasQuery.refetch()
            if (!idea?.user) {
              assignIdea(idea)
            }
          }
        },
        () => {},
      )
    } else {
      ideaIds.forEach((i) => {
        const authToken = localStorage.getItem(`ideaAuthToken:${i}`)
        api.ideas.get(
          i,
          authToken,
          (idea) => {
            myIdeasQuery.refetch()
            if (!idea?.user) {
              assignIdea(idea)
            }
          },
          () => {},
        )
      })
    }
  }, [challengeId, userId, navigate, assignIdea, location.search, myIdeasQuery])

  const newIdea = useCallback(() => {
    window.scrollTo(0, 0)
    setHasClickedNewIdea(true)
    if (ipStatement && ipManagementInfo.ipStatementEnabled && !hasSignedStatement) {
      setIpStatementAgreementOpen(true)
      return
    }
    setCreatingNewIdea(true)
    util.newIdea(challenge, user, (idea) => {
      navigate(`/ideas/${idea._id}`)
      if (user) {
        reloadUser()
        onFollow("challenge", idea.challenge)
      }
      setHasClickedNewIdea(false)
      setCreatingNewIdea(false)
    })
  }, [user, challenge, navigate, onFollow, hasSignedStatement, ipStatement, ipManagementInfo, reloadUser])

  useEffect(() => {
    if (
      hasClickedNewIdea &&
      !creatingNewIdea &&
      ipStatement &&
      ipManagementInfo.ipStatementEnabled &&
      hasSignedStatement
    ) {
      newIdea()
    }
  }, [hasSignedStatement, newIdea, ipStatement, ipManagementInfo, hasClickedNewIdea, creatingNewIdea])

  const redirectToLogin = useCallback(
    (_evt, orgCode) => {
      const currOrgCode = orgCode || challenge?.ownerOrganisation?.code
      const searchParams = new SearchParams(window.location.search)
      if (user && currOrgCode) {
        const isDevMode = import.meta.env.VITE_STAGE === "Dev"
        if (isDevMode) {
          window.location.href = `http://localhost:4000/login?code=${currOrgCode}&utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/ideas/new`
        } else {
          window.location.href = `https://${currOrgCode}.simplydo.co.uk/login?code=${currOrgCode}&utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/ideas/new`
        }
      } else {
        navigate(
          `/login?utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/ideas/new`,
        )
      }
    },
    [challengeId, challenge, navigate, user],
  )

  const toggleStamp = useCallback(
    (stamp) => {
      const newAllStamps = Object.assign([], allStamps)
      if (stamp in constants.legacyStampMapper) {
        if (!allStamps.includes(stamp) && !allStamps.includes(constants.legacyStampMapper[stamp])) {
          newAllStamps.push(stamp)
          setAllStamps(newAllStamps)
        }
      } else if (!allStamps.includes(stamp)) {
        newAllStamps.push(stamp)
        setAllStamps(newAllStamps)
      }
    },
    [allStamps],
  )

  const onClickProjectBoardLink = useCallback(() => {
    const localStorageIsSupported = util.localStorageIsSupported()
    const hasPreviouslySeenProjectBoardInfo = localStorageIsSupported
      ? !!localStorage.getItem("hasSeenProjectBoardInfo")
      : true
    if (ideaViewType === "board" && location.pathname === `/challenges/${challengeId}/ideas`) {
      if (!hasPreviouslySeenProjectBoardInfo) {
        toast(
          `The ${t("generic.challenge")} project board has been moved into the "${t("common:capitalise", { key: "generic.ideas" })}" page. You are already in the right place.`,
          {
            duration: 100000,
            icon: "ℹ️",
          },
        )
        if (localStorageIsSupported) {
          localStorage.setItem("hasSeenProjectBoardInfo", true)
        }
      }
    } else if (localStorageIsSupported && !hasPreviouslySeenProjectBoardInfo) {
      toast(
        `The ${t("generic.challenge")} project board has been moved into the "${t("common:capitalise", { key: "generic.ideas" })}" page. You can still use the sidebar link to access it, or you can change your ${t("generic.idea")} view directly from the "${t("common:capitalise", { key: "generic.ideas" })}" page.`,
        { duration: 100000, icon: "ℹ️" },
      )
      localStorage.setItem("hasSeenProjectBoardInfo", true)
    }
    updateIdeaViewType("board")
    navigate(`/challenges/${challengeId}/ideas?queryView=board`)
  }, [challengeId, navigate, updateIdeaViewType, ideaViewType, location, t])

  const onClickIdeasLink = useCallback(() => {
    const localStorageIsSupported = util.localStorageIsSupported()
    if (localStorageIsSupported) {
      const ideaViewPreference = localStorage.getItem("ideaViewTypePreference")
      updateIdeaViewType(["card", "list"].includes(ideaViewPreference) ? ideaViewPreference : "card")
    } else {
      updateIdeaViewType("card")
    }
    navigate(`/challenges/${params.id}/ideas`)
  }, [navigate, params.id, updateIdeaViewType])

  const ipStatementHandled =
    !util.organisationFeaturesEnabled(user, ["ipManagement"]) ||
    !ipManagementInfo.ipStatementEnabled ||
    (ipManagementInfo.ipStatementEnabled && ipManagementInfo.userHasSigned)

  const PageLoadErrorMessage = useMemo(() => {
    if (!challengeQuery.isError) {
      return null
    }
    let errorMessageTitle = t(user ? "challenge.errorUser" : "challenge.errorNoUser")
    let errorMessageContent = challengeQuery?.error?.message || "Unable to load at this time"
    const usingOrg = user ? user.ownerOrganisation : urlOrganisation
    if (window.location.pathname === `/challenges/${challengeId}/ideas/new`) {
      errorMessageTitle = t("challenge.errorNewIdea")
      errorMessageContent = `Please contact an administrator${usingOrg ? ` for ${usingOrg?.name} ` : " "}to request permission if you think this is incorrect.`
    }
    return <Message header={errorMessageTitle} content={errorMessageContent} />
  }, [challengeId, t, user, urlOrganisation, challengeQuery])

  const unsubmittedAssessments = useMemo(
    () =>
      myAssignedAssessments?.filter(
        (item) => !item?.assessments?.some((assessment) => assessment?.isSubmitted === true),
      ),
    [myAssignedAssessments],
  )

  const challengeIdeasProps = {
    challenge,
    newIdea,
    popularTags,
    updatePopularTags: setPopularTags,
    allStamps,
    availableGroups,
    availableOrganisations,
    updateAllStamps: setAllStamps,
    toggleStamp,
    setAvailableGroups,
    setAvailableOrganisations,
  }

  const canViewIdeasTab = useMemo(() => {
    return (
      challenge?.ideaVisibility === "users" ||
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewIdeas", challengeId)
    )
  }, [challenge, canManageChallenge, user, challengeId])

  const canViewProjectBoardTab = useMemo(() => {
    return (
      user &&
      (canManageChallenge ||
        challenge?.projectManagementVisibility === "users" ||
        util.hasPermission(user, "challenge.viewProjectBoard", challengeId))
    )
  }, [user, canManageChallenge, challengeId, challenge])

  const canViewActivityTab = useMemo(() => {
    return user && (canManageChallenge || util.hasPermission(user, "challenge.viewActivity", challengeId))
  }, [user, canManageChallenge, challengeId])

  const canViewPlannerTab = useMemo(() => {
    return (
      user &&
      challengePlannerEnabled &&
      (canManageChallenge || util.hasPermission(user, "challenge.viewPlanner", challengeId))
    )
  }, [user, canManageChallenge, challengeId, challengePlannerEnabled])

  const canViewAssessmentsTab = useMemo(() => {
    return (
      user &&
      (myAssignedAssessments?.length > 0 ||
        ((canManageChallenge || util.hasPermission(user, "challenge.viewAssessments", challengeId)) &&
          challenge?.ownerIdeaTemplate?.assessments?.rounds?.length > 0))
    )
  }, [user, canManageChallenge, challengeId, myAssignedAssessments, challenge])

  // If the user has a preferred idea view type of board, but is not allowed to view the project board on this challenge, we adjust it back to card
  if (ideaViewType === "board") {
    if (!canViewProjectBoardTab) {
      updateIdeaViewType("card")
      setSearchParams((prev) => {
        prev.delete("queryView")
        return prev
      })
    }
  }

  if (PageLoadErrorMessage) return <PageLoadError title={PageLoadErrorMessage} />
  return (
    <div>
      {challenge && (
        <Helmet>
          <title>{challenge ? challenge.name : t("common:capitalise", { key: "generic.challenge" })}</title>
          <meta
            property="og:title"
            content={challenge ? challenge.name : t("common:capitalise", { key: "generic.challenge" })}
          />
          <meta property="og:image" content={challenge.bannerImageUrl} />
          <meta property="og:url" content={`${util.host(user)}/challenges/${challenge._id}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@_simplydo"></meta>
          <meta name="twitter:url" content={`${util.host(user)}/challenges/${challenge._id}`} />
          <meta name="twitter:title" content={challenge.name} />
          <meta name="twitter:image" content={challenge.bannerImageUrl} />
        </Helmet>
      )}
      {util.organisationFeaturesEnabled(user, ["ipManagement"]) ? (
        <ChallengeIPStatementAgreement
          user={user}
          challenge={challenge}
          isOpen={ipStatementAgreementOpen}
          setHasSignedStatement={setHasSignedStatement}
          onClose={() => setIpStatementAgreementOpen(false)}
          ipManagementInfo={ipManagementInfo}
          setIpManagementInfo={setIpManagementInfo}
          ipStatement={ipStatement}
          setIpStatement={setIpStatement}
        />
      ) : null}
      {user && (
        <ChallengeStatusBar
          challengeId={challengeId}
          loading={challengeQuery.isLoading}
          challenge={challenge}
          newIdea={newIdea}
          myIdeas={myIdeas}
          overviewPage={true}
          redirectToLogin={redirectToLogin}
        />
      )}
      <div>
        <ChallengeHeader
          loading={challengeQuery.isLoading}
          challenge={challenge}
          myIdeas={myIdeas}
          newIdea={newIdea}
          isIdeasPage={true}
          redirectToLogin={redirectToLogin}
        />
        <SideNav
          fluid
          collapsible
          isCollapsed={!!isCollapsed}
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => setIsCollapsed(false)}
          loading={challengeQuery.isLoading}
          top={120}
          navItems={
            challengeQuery.isLoading
              ? [
                  {
                    key: "loading",
                    type: "custom",
                    content: (
                      <PlaceholderStack gap={15}>
                        <PlaceholderItem height={40} />
                        <PlaceholderItem height={40} />
                      </PlaceholderStack>
                    ),
                  },
                ]
              : [
                  {
                    key: "overview",
                    to: ".",
                    content: t("challenge.details.title"),
                    iconLeft: "info circle",
                    active: window.location.pathname === `/challenges/${params.id}`,
                  },
                  canViewIdeasTab
                    ? {
                        key: "ideas",
                        content: `${t("common:capitalise", { key: "generic.ideas" })}`,
                        iconLeft: "lightbulb",
                        active:
                          window.location.pathname === `/challenges/${params.id}/ideas` && ideaViewType !== "board",
                        onClick: onClickIdeasLink,
                        rightIcon:
                          user && challenge?.ideaCount > 0 ? (
                            <StyledLabel size="tiny" circular>
                              {challenge?.ideaCount || 0}
                            </StyledLabel>
                          ) : null,
                      }
                    : null,

                  canViewActivityTab
                    ? {
                        key: "activity",
                        to: `activity`,
                        content: "Activity",
                        iconLeft: "chart line",
                        active: window.location.pathname === `/challenges/${params.id}/activity`,
                      }
                    : null,

                  canViewAssessmentsTab
                    ? {
                        key: "assessments",
                        to: `assessments`,
                        content: !canManageChallenge ? "Your assessments" : "Assessment",
                        iconLeft: "percent",
                        rightIcon:
                          unsubmittedAssessments?.length > 0 ? (
                            <StyledLabel size="tiny" color="red" circular>
                              {unsubmittedAssessments?.length}
                            </StyledLabel>
                          ) : null,
                        active: window.location.pathname.indexOf(`/challenges/${params.id}/assessments`) > -1,
                      }
                    : null,

                  challenge?.questionsEnabled
                    ? {
                        key: "questions",
                        to: `questions`,
                        content: "Questions",
                        iconLeft: "talk",
                        active: window.location.pathname.indexOf(`/challenges/${params.id}/questions`) > -1,
                        rightIcon: (
                          <>
                            {challenge?.publicQuestions > 0 ? (
                              <StyledLabel size="tiny" circular>
                                {challenge.publicQuestions}
                              </StyledLabel>
                            ) : null}
                            {challenge?.nonPublicQuestions > 0 ? (
                              <StyledLabel size="tiny" circular color="red">
                                {challenge.publicQuestions}
                              </StyledLabel>
                            ) : null}
                          </>
                        ),
                      }
                    : null,

                  canViewPlannerTab
                    ? {
                        key: "planner",
                        to: `planner`,
                        content: "Planner",
                        iconLeft: "calendar",
                        active: window.location.pathname.indexOf(`/challenges/${params.id}/planner`) > -1,
                      }
                    : null,

                  canViewProjectBoardTab
                    ? {
                        key: "projectManagement",
                        content: "Project Board",
                        iconLeft: "compass",
                        onClick: onClickProjectBoardLink,
                        active:
                          window.location.pathname === `/challenges/${params.id}/ideas` && ideaViewType === "board",
                      }
                    : null,
                ].filter((item) => Boolean(item))
          }
          main={
            challengeQuery?.isLoading ? (
              <PlaceholderStack gap={20} style={{}}>
                <PlaceholderItem height={40} style={{ minWidth: "100%" }} />
                <PlaceholderItem height={250} style={{ minWidth: "100%" }} />
                {window.location.pathname === `/challenges/${params.id}` && (
                  <>
                    <PlaceholderItem height={40} style={{ minWidth: "100%", marginTop: 20 }} />
                    <PlaceholderItem height={150} style={{ minWidth: "100%" }} />
                  </>
                )}{" "}
              </PlaceholderStack>
            ) : (
              <Routes>
                <Route
                  path="*"
                  element={
                    <ChallengeOverview
                      challenge={challenge}
                      newIdea={newIdea}
                      ipStatement={ipStatement}
                      ipManagementInfo={ipManagementInfo}
                      ipStatementHandled={ipStatementHandled}
                      setIpStatementAgreementOpen={setIpStatementAgreementOpen}
                      redirectToLogin={redirectToLogin}
                    />
                  }
                />
                {canViewProjectBoardTab ? (
                  <Route
                    path="board"
                    element={
                      <ChallengeIdeasProjectWrapper>
                        <ChallengeIdeas {...challengeIdeasProps} />
                      </ChallengeIdeasProjectWrapper>
                    }
                  />
                ) : null}
                <Route path="ideas/new" element={<NewIdea redirectToLogin={redirectToLogin} />} />
                {(ideaViewType === "board" ? canViewProjectBoardTab : canViewIdeasTab) && (
                  <Route path="ideas" element={<ChallengeIdeas {...challengeIdeasProps} />} />
                )}
                {canViewPlannerTab && <Route path="planner/*" element={<ChallengePlanner challenge={challenge} />} />}
                {canViewAssessmentsTab && (
                  <Route path="assessments/*" element={<ChallengeAssessments challenge={challenge} />} />
                )}
                {canViewActivityTab && <Route path="activity" element={<ChallengeActivity challenge={challenge} />} />}
                {challenge?.questionsEnabled && (
                  <Route path="questions" element={<ChallengeQuestions challenge={challenge} />} />
                )}
              </Routes>
            )
          }
          afterContent={
            <>
              <Divider />
              <ReportContentChip forType="challenge" forId={challengeId} isCollapsed={isCollapsed} />
            </>
          }
        />
      </div>
    </div>
  )
}

export default Challenge
